<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/authStore';
import { useSoloStore } from '@/stores/soloStore';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const authStore = useAuthStore();
const soloStore = useSoloStore();

// MVP popup in-app (pas de vraie notification OS, cf. TCHINQUIZ.txt § Mode Solo) :
// affiché une fois par jour tant que le défi du jour n'est pas joué. Ne s'affiche
// pas pendant une partie en cours ni sur les écrans du mode Solo lui-même.
const DISMISS_KEY = 'tchinquiz.daily.popup.dismissed.v1';
const visible = ref(false);

onMounted(async () => {
  if (!authStore.isLoggedIn) return;
  const routeName = String(route.name ?? '');
  if (routeName === 'game' || routeName.startsWith('solo')) return;
  try {
    await soloStore.fetchDaily();
  } catch {
    return;
  }
  const daily = soloStore.daily;
  if (!daily || daily.already_played) return;
  if (localStorage.getItem(DISMISS_KEY) === daily.challenge_date) return;
  visible.value = true;
});

function dismiss() {
  if (soloStore.daily) localStorage.setItem(DISMISS_KEY, soloStore.daily.challenge_date);
  visible.value = false;
}

function play() {
  dismiss();
  router.push('/solo/daily');
}
</script>

<template>
  <div v-if="visible" class="popup-overlay" @click.self="dismiss">
    <div class="popup-card">
      <div class="popup-emoji">🎲</div>
      <h2 class="h3">{{ t('dailyPopup.title') }}</h2>
      <p class="muted">{{ t('dailyPopup.body') }}</p>
      <div class="popup-actions">
        <button class="btn-ghost" @click="dismiss">{{ t('dailyPopup.later') }}</button>
        <button class="btn" @click="play">{{ t('dailyPopup.playNow') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
}
.popup-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 24px;
  max-width: 320px;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}
.popup-emoji { font-size: 48px; }
.popup-actions { display: flex; gap: 10px; width: 100%; margin-top: 12px; }
.popup-actions button { flex: 1; }
</style>
